'use client';

/**
 * Client wrapper for FAQSection that handles query submission logic.
 *
 * This wrapper:
 * 1. Handles file uploads to S3 via pre-signed URLs
 * 2. Sends the query with attachment references
 * 3. Manages loading/error states
 * 4. Shows toast notifications
 */
import { useState } from 'react';
import { toast } from 'sonner';

import type { AskQueryFormValues } from '@ui/components/features/landing/forms/AskQueryForm';
import { FAQSection, FAQSectionProps } from '@ui/sections/faq/FAQSection';
import { BrowserApiClient } from '@website/lib/api/browser';
import { collectClientContext } from '@website/utils/collectClientContext';

type FAQSectionWrapperProps = Omit<FAQSectionProps, 'onSubmit' | 'isSubmitting'> & {
  /**
   * Translations for success/error messages
   */
  messageTranslations?: {
    querySuccessMessage?: string;
    querySuccessDescription?: string;
    queryErrorPrefix?: string;
  };
};

export const FAQSectionWrapper: React.FC<FAQSectionWrapperProps> = ({ messageTranslations, ...props }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Handle form submission with file uploads
   */
  const handleSubmit = async (data: AskQueryFormValues, attachments?: File[]) => {
    setIsSubmitting(true);

    try {
      let attachmentKeys: string[] = [];

      // Step 1: Upload attachments if present
      if (attachments && attachments.length > 0) {
        for (const file of attachments) {
          // Get pre-signed URL
          const uploadResponse = await BrowserApiClient.getPrivateAssetUploadUrl({
            filename: file.name,
            type: file.type
          });

          // Upload file to pre-signed URL
          const uploadResult = await fetch(uploadResponse.uploadUrl, {
            method: 'PUT',
            body: file,
            headers: {
              'Content-Type': file.type
            }
          });

          if (!uploadResult.ok) {
            throw new Error('Failed to upload attachment');
          }

          attachmentKeys.push(uploadResponse.key);
        }
      }

      // Step 2: Send query (email is optional for logged-in users)
      const queryResponse = await BrowserApiClient.sendQuery({
        ...(data.email && { email: data.email }),
        message: data.question,
        attachments: attachmentKeys,
        clientContext: collectClientContext()
      });

      if (queryResponse.success) {
        toast.success(messageTranslations?.querySuccessMessage || 'Message sent!', {
          id: 'faq-query-success',
          description: messageTranslations?.querySuccessDescription || 'We will reply to you via email shortly.'
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      toast.error(`${messageTranslations?.queryErrorPrefix || 'Failed to send query:'} ${errorMessage}`, {
        id: 'faq-query-error'
      });
      console.error('Query submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return <FAQSection {...props} onSubmit={handleSubmit} isSubmitting={isSubmitting} />;
};
